// FILE: src/contexts/LearnerAuthContext.js
//
// Learner-side auth context. Mirrors the AdminGate context pattern but is
// independent of it: one user can be both an admin AND a learner without the
// two sessions interfering.
//
// Responsibilities:
//   • Subscribe to supabase.auth state changes.
//   • Expose { user, session, profile, loading, signOut, refresh } via hook.
//   • On the first sign-in (no learner_profiles row yet), upsert a profile
//     row so the rest of the journey machinery has somewhere to read from.
//
// What this does NOT do:
//   • Block anonymous use anywhere. The Provider always renders its children.
//   • Talk to the AdminGate. Admin-vs-learner is fully decoupled.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { supabase } from '../services/supabaseClient';
import { upsertLearnerProfile, getLearnerProfile } from '../services/learnerProfileService';
import { trackEvent, getAnonId, firstTouchMetadata } from '../services/telemetryService';
import { fireAchievement, ACHIEVEMENT } from '../services/achievementService';
import { isFeatureEnabled } from '../config/featureFlags';

const LearnerAuthContext = createContext({
  user:    null,
  session: null,
  profile: null,
  loading: true,
  signOut: async () => {},
  refresh: async () => {},
});

export function LearnerAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Guard against double-upsert during React strict-mode dev re-mount.
  const profileEnsuredFor = useRef(new Set());

  const ensureProfile = useCallback(async (nextUser) => {
    if (!nextUser?.id) return null;
    if (profileEnsuredFor.current.has(nextUser.id)) return null;
    profileEnsuredFor.current.add(nextUser.id);

    // Conversion telemetry: a missing profile row means this is the learner's
    // first successful auth → registration completed. Detected BEFORE the
    // upsert creates the row. Best-effort and fully guarded — never blocks or
    // alters sign-in (and tolerates environments where getLearnerProfile is
    // unavailable, e.g. unit-test mocks).
    let wasNewProfile = false;
    try {
      if (typeof getLearnerProfile === 'function') {
        const existing = await getLearnerProfile(nextUser.id);
        wasNewProfile = !existing;
      }
    } catch (_) {
      wasNewProfile = false;
    }

    try {
      const row = await upsertLearnerProfile({
        userId:      nextUser.id,
        // Explicit display_name (optional email-signup field) wins; otherwise
        // fall back to the OAuth provider's profile name — Google/Microsoft
        // populate full_name / name in user_metadata. Mig 086 backfills
        // existing rows the same way.
        displayName:
          nextUser.user_metadata?.display_name ||
          nextUser.user_metadata?.full_name ||
          nextUser.user_metadata?.name ||
          null,
        // IANA timezone from the browser; nullable.
        timezone:
          typeof Intl !== 'undefined' && Intl.DateTimeFormat
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : null,
      });
      if (row) setProfile(row);

      if (wasNewProfile) {
        try {
          const anonId = getAnonId({ createIfMissing: false });
          // Tag completed registrations with the auth provider so the funnel
          // can split email vs google vs microsoft. Supabase reports the
          // provider as 'email' | 'google' | 'azure' — normalise 'azure' to
          // 'microsoft' to match the OAuth call site in LearnerAuth.js.
          const rawProvider = nextUser?.app_metadata?.provider || null;
          const method = rawProvider === 'azure' ? 'microsoft' : (rawProvider || 'unknown');
          // Echo first-touch acquisition (ft_*) so the conversion row itself
          // records what originally acquired this browser (Content Factory
          // Stage 2). Empty object when no record exists.
          trackEvent('register_completed', 'growth', { had_anon_id: !!anonId, method, ...firstTouchMetadata() });
          // The key conversion metric: an anonymous learner became registered.
          if (anonId) trackEvent('anonymous_to_registered', 'growth', { from: anonId, method });
        } catch (_) { /* telemetry never blocks sign-in */ }

        // Founding Tester: every learner who completes registration during the
        // beta earns the badge. Fire-and-forget through the idempotent
        // record_achievement RPC; it dispatches 'pm-pixel:achievement-earned'
        // when newly earned, which HeaderUserMenu turns into the welcome modal.
        try {
          // Founding Tester enrollment is closed (2026-08) — no NEW automatic
          // awards. Existing earned achievements are untouched; the curated
          // founding_testers table (admin-managed) is unaffected.
          if (isFeatureEnabled('foundingTesterEnrollmentOpen')) {
            fireAchievement({ key: ACHIEVEMENT.FOUNDING_TESTER, context: { source: 'registration' } });
          }
        } catch (_) { /* achievement never blocks sign-in */ }
      }
      return row;
    } catch (err) {
      // Profile upsert never blocks sign-in.
      console.warn('[LearnerAuthContext] profile upsert failed:', err?.message || err);
      return null;
    }
  }, []);

  // Initial session probe + subscription.
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error) {
          console.warn('[LearnerAuthContext] getSession error:', error.message);
        }
        const sess = data?.session || null;
        setSession(sess);
        setUser(sess?.user || null);
        if (sess?.user) {
          // Fire-and-forget — we don't block readiness on profile creation.
          ensureProfile(sess.user);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();

    const sub = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (cancelled) return;
      setSession(nextSession || null);
      const nextUser = nextSession?.user || null;
      setUser(nextUser);
      // Telemetry: returning-user signal. Strictly on SIGNED_IN so token
      // refreshes / user updates don't inflate the count.
      if (event === 'SIGNED_IN') {
        try { trackEvent('signin', 'growth'); } catch (_) { /* never blocks */ }
      }
      if (nextUser) {
        ensureProfile(nextUser);
      } else {
        setProfile(null);
      }
    });

    return () => {
      cancelled = true;
      try {
        sub?.data?.subscription?.unsubscribe?.();
      } catch (_) {
        /* no-op */
      }
    };
  }, [ensureProfile]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('[LearnerAuthContext] signOut failed:', err?.message || err);
    }
    profileEnsuredFor.current.clear();
    setSession(null);
    setUser(null);
    setProfile(null);
  }, []);

  // Refetch session + profile. NOTE: this must NOT go through ensureProfile —
  // that helper has a once-per-user guard (profileEnsuredFor) to avoid double
  // upserts, so it returns early for an already-loaded user and would NOT pick
  // up freshly-changed columns. We read the row directly so callers (recalibrate,
  // exam-date set, baseline auto-set) actually see updated fields.
  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const sess = data?.session || null;
    setSession(sess);
    const nextUser = sess?.user || null;
    setUser(nextUser);
    if (nextUser?.id) {
      const row = await getLearnerProfile(nextUser.id);
      if (row) setProfile(row);
    }
  }, []);

  // Auto-refresh the profile when a baseline is set. sessionService dispatches
  // 'pm-pixel:baseline-set' after writing baseline_at_<cert>; without this the
  // dashboard / readiness keep showing the "Set a <cert> baseline" prompt for a
  // cert the learner just completed (stale profile).
  useEffect(() => {
    function onBaselineSet() { refresh(); }
    window.addEventListener('pm-pixel:baseline-set', onBaselineSet);
    return () => window.removeEventListener('pm-pixel:baseline-set', onBaselineSet);
  }, [refresh]);

  const value = { user, session, profile, loading, signOut, refresh };
  return (
    <LearnerAuthContext.Provider value={value}>
      {children}
    </LearnerAuthContext.Provider>
  );
}

export function useLearnerAuth() {
  return useContext(LearnerAuthContext);
}

// Exported for tests that need to render with a custom value.
export { LearnerAuthContext };
